import clsx from "clsx";

import { LoadingSlide } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { FETCH_SPEND_DATA_COLUMNS } from "@gql/mmm-scenario-modeling";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type SpendDataColumns,
  type SpendDataColumnsVariables,
} from "@north-beam/nb-client-side";
import { type FullBudgetMixResponse } from "@north-beam/nb-common";
import { ExploreBudgetsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { useMMMAdminMode } from "../../../budget-mix-table/utils";

import {
  handleExportAdminCreationMetadata,
  handleExportBudgetMix,
  handleExportScenario,
} from "./utils";

type Props = {
  budgetMix: FullBudgetMixResponse;
};

export function ExportBudgetMix({ budgetMix }: Props) {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);
  const { user } = useUser();
  const { kpis } = useClientKpis();
  const isAdminMode = useMMMAdminMode(user);
  const { selectedCycle } = useSelectedCycle();

  const { data: spendData, loading } = useNorthbeamQuery<
    SpendDataColumns,
    SpendDataColumnsVariables
  >(FETCH_SPEND_DATA_COLUMNS, {
    // TODO FIXME!!!!
    // 1. This gets called repeatedly for some reason
    // 2. We should never be using the modelPath for queries. Use the
    //    model ID and get the model path on the backend.
    // 3. We should be getting spend data columns from the ModelDataColumns
    //    saved in DM
    variables: { modelPath: selectedCycle.model.model_path },
  });

  return (
    <Menu as="div" className="relative z-1">
      {({ open: isOpen }) => (
        <>
          <GenericTooltip content="Export" noInfoCircle>
            <MenuButton
              className={clsx("rounded-md h-6 w-6 flex-center text-sm", {
                "text-cosmic hover:bg-cosmic-2": !isOpen,
                "text-white bg-cosmic": isOpen,
              })}
            >
              <i className="fa-regular fa-download"></i>
            </MenuButton>
          </GenericTooltip>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <MenuItems
              className={clsx(
                "absolute rounded-md bg-v2-primary py-1 text-sm elevation-1 w-56 overflow-hidden right-0",
              )}
            >
              <div className="font-bold px-3 py-2 text-interstellar+2">
                Export as
              </div>
              {loading && <LoadingSlide />}
              {!loading && (
                <>
                  <MenuItem
                    as="div"
                    onClick={() => {
                      trackMMMEvent({
                        component:
                          ExploreBudgetsTrackedComponent.EXPORT_RECOMMENDED_BUDGET_MIX_SELECTOR,
                        action: "onClick",
                        actionInfo: "Export recommended budget mix summary",
                      });

                      handleExportBudgetMix(
                        budgetMix,
                        kpis,
                        user.currencies.currentCurrency,
                      );
                    }}
                  >
                    {({ focus }) => (
                      <div
                        className={clsx("px-3 py-2 hover:cursor-pointer", {
                          "bg-v2-secondary": focus,
                        })}
                      >
                        <div className="flex items-center space-x-2">
                          <i className="fa-regular fa-file-csv text-cosmic" />
                          <div>Total period budget and KPIs</div>
                        </div>
                        <div className="text-gray+1 text-xxs mt-1">
                          Includes total period budget and the forecasted KPIs
                          shown in this page
                        </div>
                      </div>
                    )}
                  </MenuItem>
                  <MenuItem
                    as="div"
                    onClick={() => {
                      trackMMMEvent({
                        component:
                          ExploreBudgetsTrackedComponent.EXPORT_RECOMMENDED_BUDGET_MIX_SELECTOR,
                        action: "onClick",
                        actionInfo: "Export recommended budget mix spend",
                      });

                      handleExportScenario(
                        budgetMix,
                        spendData?.me.mmm.spendDataColumns.spendDataColumns,
                        user.currencies.currentCurrency,
                      );
                    }}
                  >
                    {({ focus }) => (
                      <div
                        className={clsx("px-3 py-2 hover:cursor-pointer", {
                          "bg-v2-secondary": focus,
                        })}
                      >
                        <div className="flex items-center space-x-2">
                          <i className="fa-regular fa-file-csv text-cosmic" />
                          <div>Weekly spend sheet</div>
                        </div>
                        <div className="text-gray+1 text-xxs mt-1">
                          Modify and upload in the &#8216;Create your own
                          budgets&#8217; tab to create a custom budget
                        </div>
                      </div>
                    )}
                  </MenuItem>
                  {isAdminMode && (
                    <MenuItem
                      as="div"
                      onClick={() => {
                        trackMMMEvent({
                          component:
                            ExploreBudgetsTrackedComponent.EXPORT_RECOMMENDED_BUDGET_MIX_SELECTOR,
                          action: "onClick",
                          actionInfo:
                            "Export recommended budet mix creation metadata",
                        });

                        handleExportAdminCreationMetadata(
                          budgetMix.name ?? String(budgetMix.id),
                          budgetMix.create_budget_recommendations_metadata,
                        );
                      }}
                    >
                      {({ focus }) => (
                        <div
                          className={clsx("px-3 py-2 hover:cursor-pointer", {
                            "bg-v2-secondary": focus,
                          })}
                        >
                          <div className="flex items-center space-x-2">
                            <i className="fa-regular fa-file-text text-cosmic" />
                            <div>Creation Metadata JSON</div>
                          </div>
                          <div className="text-gray+1 text-xxs mt-1">
                            (Admin only)
                          </div>
                        </div>
                      )}
                    </MenuItem>
                  )}
                </>
              )}
            </MenuItems>
          </Transition>
        </>
      )}
    </Menu>
  );
}
